import { EventEmitter, Injectable, NgZone } from '@angular/core';
import { AngularFireAuth } from '@angular/fire/compat/auth';
import { Observable, ReplaySubject, firstValueFrom, shareReplay } from 'rxjs';
import { Router } from '@angular/router';
import { tap } from 'rxjs/operators';
import firebase from 'firebase/compat';
import User = firebase.User;
import { LoginDeeplinkService } from '../login-deeplink/login-deeplink.service';
import {
  GoogleAnalyticsService,
  GTMEventloginFailure,
  GTMEventloginSuccess,
  GTMEventlogout,
} from '../google-analytics.service';
import { HttpClient, HttpHeaders } from '@angular/common/http';
import { environment } from '../../../environments/environment';
import { AngularFireMessaging } from '@angular/fire/compat/messaging';

import { Title } from '@angular/platform-browser';

import {
  ActionPerformed,
  DeliveredNotifications,
  PushNotificationSchema,
  PushNotifications,
} from '@capacitor/push-notifications';
import { Capacitor } from '@capacitor/core';
import { TranslateService } from '@ngx-translate/core';
import { App } from '@capacitor/app';
import { BaseAuthenticationService } from '@nx-c4g/c4g-auth';

@Injectable({
  providedIn: 'root',
})
export class AuthenticationService extends BaseAuthenticationService {
  device: string;
  initialized = new ReplaySubject<boolean>();
  stateChange: EventEmitter<any> = new EventEmitter<any>();
  private _isAdmin: boolean = null;
  /** Session flag when admin opened the CG app via “login on behalf” (MongoDB hints on dashboard). */
  private static readonly ON_BEHALF_SESSION_KEY = 'c4g_cg_on_behalf';

  constructor(
    private http: HttpClient,
    private ngZone: NgZone,
    private loginDeeplinkService: LoginDeeplinkService,
    protected override angularFireAuth: AngularFireAuth,
    private translateService: TranslateService,
    public gaService: GoogleAnalyticsService,
    private angularFireMessaging: AngularFireMessaging,
    private titleService: Title,
    private router: Router
  ) {
    super(angularFireAuth);
    this.angularFireAuth.setPersistence('local').then(() => {
      console.log('[AuthService] Firebase persistence set to local');
    }).catch(error => {
      console.error('[AuthService] Error setting persistence:', error);
    });
  }

  protected override onLogin(user: any): void {
    this.gaService.pushTag(GTMEventloginSuccess);
    if (Capacitor.isNativePlatform()) {
      this.setupPushNotification();
      this.setupAppStateListener();
    }
    this.initialized.next(true);
  }

  protected override onLogout(): void {
    this.gaService.pushTag(GTMEventlogout);
    this.initialized.next(true);
  }

  protected override onUserStateChange(user: any): void {
    // Add any additional app-specific logic on user state change if needed
  }

  override async SignUp(email: string, password: string): Promise<firebase.auth.UserCredential> {
    const res = await this.angularFireAuth.createUserWithEmailAndPassword(
      email.toLowerCase().trim(),
      password
    );
    if (res?.user) {
      this.user = res.user;
      this.setIdToken(await res.user.getIdToken());
      this.loggedInUser = true;
      try {
        await firstValueFrom(
          this.http.post(`${environment.api}auth/send-verification-email`, {
            email: res.user.email,
            source: 'caregiver',
          })
        );
      } catch (e) {
        console.error('Failed to send verification email', e);
      }
    }
    return res;
  }

  async getUserIfLoggedIn() {
    return new Promise((resolve, reject) => {
      this.angularFireAuth.authState.subscribe({
        next: async (user) => {
          if (!user) {
            this.user = null;
            this.loggedInUser = false;
            this.setIdToken(null);
            reject('User not logged in');
          }
          try {
            const token = await user.getIdToken(true);
            this.setIdToken(token);
            this.loggedInUser = true;
            resolve(user);
          } catch (error) {
            console.error('[AuthService] Error getting user token:', error);
            this.user = null;
            this.loggedInUser = false;
            this.setIdToken(null);
            reject(error);
          }
        },
        error: async (err) => {
          this.user = null;
          this.loggedInUser = false;
          this.setIdToken(null);
          reject(err);
        },
      });
    });
  }

  async waitForInit() {
    return firstValueFrom(this.initialized);
  }

  // Always get isAdmin$ as a method so it uses the latest environment
  isAdmin$() {
    // When called, update the cache
    const obs$ = this.http.get<boolean>(environment.api + 'admin/isAdmin').pipe(shareReplay(1));
    obs$.subscribe(val => this._isAdmin = val);
    return obs$;
  }

  // Backward-compatible synchronous method
  isAdmin(): boolean {
    return this._isAdmin;
  }

  /** True after sign-in with behalftoken in this tab (cleared on normal login or sign-out). */
  isOnBehalfSession(): boolean {
    try {
      return sessionStorage.getItem(AuthenticationService.ON_BEHALF_SESSION_KEY) === '1';
    } catch {
      return false;
    }
  }

  private static setOnBehalfSessionFlag(on: boolean): void {
    try {
      if (on) {
        sessionStorage.setItem(AuthenticationService.ON_BEHALF_SESSION_KEY, '1');
      } else {
        sessionStorage.removeItem(AuthenticationService.ON_BEHALF_SESSION_KEY);
      }
    } catch {
      /* ignore (e.g. storage blocked) */
    }
  }

  override SignOut(): void {
    AuthenticationService.setOnBehalfSessionFlag(false);
    super.SignOut();
  }

  private async setupPushNotification() {
    console.log('Push registration Init V2');

    console.log('addListener: registration');
    await PushNotifications.addListener('registration', (token) => {
      console.log('Push Registration token: ', token.value);
      this.saveDevice(token.value);
    });

    console.log('addListener: registrationError');
    await PushNotifications.addListener('registrationError', (err) => {
      console.error('Push Registration error: ', err.error);
    });

    console.log('addListener: pushNotificationReceived');
    await PushNotifications.addListener(
      'pushNotificationReceived',
      (notification) => {
        console.log('Push notification received: ', notification);

        if (Capacitor.getPlatform() !== 'web') {
          console.log(
            'Push notification action performed',
            notification.click_action,
            notification.data
          );
          this.performClickAction(notification);
        }
      }
    );

    console.log('addListener: pushNotificationActionPerformed');
    await PushNotifications.addListener(
      'pushNotificationActionPerformed',
      (notification) => {
        console.log(
          'Push notification action performed',
          notification.actionId,
          notification.inputValue
        );
        this.performAction(notification);
      }
    );

    console.log('PushNotifications.checkPermissions()');
    let permStatus = await PushNotifications.checkPermissions();

    if (permStatus.receive === 'prompt') {
      console.log('PushNotifications.requestPermissions()');
      permStatus = await PushNotifications.requestPermissions();
    }

    if (permStatus.receive !== 'granted') {
      return;
    }

    console.log('PushNotifications.register()');
    await PushNotifications.register();

    const getDeliveredNotifications = async () => {
      const notificationList =
        await PushNotifications.getDeliveredNotifications();
      console.log('Push delivered notifications', notificationList);
    };
  }

  private performAction(notification: ActionPerformed) {
    console.log('notification performAction', notification);
    if (notification.notification.data.type) {
      switch (notification.notification.data.type) {
        case 'Extension-Request':
          {
            const extensionId = notification.notification.data.extensionId;
            this.ngZone.run(() => {
              this.router.navigateByUrl('/extensions');
            });
          }
          break;
        case 'check-in':
          this.ngZone.run(() => {
            this.router.navigateByUrl('/');
          });

          break;
        case 'check-in-late':
          this.router.navigateByUrl('/');
          break;
        case 'check-out':
          this.router.navigateByUrl('/');
          break;
        case 'check-out-late':
          this.router.navigateByUrl('/');
          break;
        case 'week-report':
          this.ngZone.run(() => {
            this.router.navigateByUrl('/time-sheets');
          });
          break;
        case 'Booking-Changed':
          this.router.navigateByUrl(
            '/appt-view/' +
              notification.notification.data.bookingId +
              '/' +
              notification.notification.data.apptId
          );
          break;
      }
    }
  }

  performClickAction(notification: PushNotificationSchema) {
    switch (notification.data.type) {
      case 'Extension-Request':
        {
          const extensionId = notification.data.extensionId;
          const result = confirm(
            this.translateService.instant('extensionRequest')
          );
          if (result) {
            this.ngZone.run(() => {
              this.router.navigateByUrl('/extensions');
            });
          }
        }
        break;
      case 'Booking-Changed':
        const result = confirm(this.translateService.instant('bookingChanged'));
        if (result) {
          console.log(
            'bookingChanged',
            '/appt-view/' +
              notification.data.bookingId +
              '/' +
              notification.data.apptId
          );
          this.ngZone.run(() => {
            this.router.navigateByUrl(
              '/appt-view/' +
                notification.data.bookingId +
                '/' +
                notification.data.apptId
            );
          });
        }
        break;
      default:
        break;
    }
    throw new Error('Method not implemented.');
  }

  async currentUser() {
    this.user = await this.angularFireAuth.currentUser;
  }

  // Add back saveDevice method
  saveDevice(device: string) {
    if (device) {
      console.log('Save Device', device);
      this.http
        .put(environment.api + 'care-giver/device', { device: device })
        .subscribe();
    }
  }

  override SignIn(email: string, password: string): Promise<boolean> {
    email = email.toLowerCase().trim();
    return new Promise<boolean>((resolve, reject) => {
      this.angularFireAuth
        .signInWithEmailAndPassword(email, password)
        .then(async (res) => {
          AuthenticationService.setOnBehalfSessionFlag(false);
          if (res && res.user && !res.user.emailVerified) {
            this.user = res.user;
            this.setIdToken(await res.user.getIdToken());
            this.loggedInUser = true;
            try {
              await firstValueFrom(
                this.http.post(
                  `${environment.api}auth/send-verification-email`,
                  { email: res.user.email, source: 'caregiver' }
                )
              );
            } catch (e) {
              console.error('Failed to send verification email', e);
            }
          }
          resolve(true);
        })
        .catch((err) => {
          resolve(false);
        });
    });
  }

  handleVerifyEmail(mode, oobCode, apiKey, lang) {
    console.log('Mode', mode);
    console.log('oobCode', oobCode);
    console.log('apiKey', apiKey);
    console.log('lang', lang);
    if (mode === 'verifyEmail') {
      this.angularFireAuth.applyActionCode(oobCode).then((result) => {
        console.log('applyActionCode', result);
      });
    }
  }

  handlePasswordChange(mode, oobCode, newpass) {
    if (mode === 'resetPassword') {
      this.angularFireAuth
        .confirmPasswordReset(oobCode, newpass)
        .then((result) => {
          console.log('applyActionCode', result);
        });
    }
  }

  signInOnBehalf(behalftoken: any): Promise<boolean> {
    console.log('[AuthService] signInOnBehalf - Current environment:', environment.name, environment);
    this.stateChange.emit(true);
    return new Promise<boolean>((resolve, reject) => {
      this.angularFireAuth
        .signInWithCustomToken(behalftoken)
        .then(async (res) => {
          this.userData = this.angularFireAuth.authState;
          AuthenticationService.setOnBehalfSessionFlag(true);
          this.gaService.pushTag(GTMEventloginSuccess);
          if (this.loginDeeplinkService.get() !== '') {
            const url = this.loginDeeplinkService.get();
            this.loginDeeplinkService.set(''); // Clear last URK
            this.router.navigateByUrl(url);
          } else {
            this.router.navigateByUrl('/');
          }
          resolve(true);
        })
        .catch((err) => {
          this.gaService.pushTag(GTMEventloginFailure);
          resolve(false);
        });
    });
  }
}
